import { useState, useEffect } from 'react'
import {
  Users,
  Search,
  Eye,
  Mail,
  Phone,
  Link2,
  X,
  UserPlus,
  Unlink,
  Building,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { partnerAdminApi } from '../services/api'
import type { Accountant, Assignment } from '../services/api'

/**
 * Accountants Management Page
 *
 * Allows partner admins to:
 * - View all accountants in the partner
 * - See client assignments per accountant
 * - Assign/unassign clients to accountants
 * - Bulk assign clients
 */
export function Accountants() {
  const [accountants, setAccountants] = useState<Accountant[]>([])
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Pagination states
  const [accountantsPage, setAccountantsPage] = useState(1)
  const [assignmentsPage, setAssignmentsPage] = useState(1)
  const itemsPerPage = 10

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showAddClientsModal, setShowAddClientsModal] = useState(false)
  const [selectedAccountant, setSelectedAccountant] = useState<Accountant | null>(null)
  const [accountantClients, setAccountantClients] = useState<any[]>([])
  const [loadingClients, setLoadingClients] = useState(false)

  // Available clients (not assigned to this accountant)
  const [availableClients, setAvailableClients] = useState<any[]>([])
  const [selectedClientsToAssign, setSelectedClientsToAssign] = useState<string[]>([])
  const [loadingAvailableClients, setLoadingAvailableClients] = useState(false)
  const [assigningClients, setAssigningClients] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Fetch data on mount
  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [accountantsRes, assignmentsRes] = await Promise.all([
        partnerAdminApi.accountants.list(),
        partnerAdminApi.accountants.getAllAssignments()
      ])

      setAccountants(accountantsRes.accountants)
      setAssignments(assignmentsRes.assignments)
    } catch (err: any) {
      console.error('Error fetching data:', err)
      setError(err.message || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  const handleViewClients = async (accountant: Accountant) => {
    setSelectedAccountant(accountant)
    setShowAssignModal(true)
    setLoadingClients(true)

    try {
      const clients = await partnerAdminApi.accountants.getClients(accountant.id)
      setAccountantClients(clients)
    } catch (err) {
      console.error('Error fetching accountant clients:', err)
      setAccountantClients([])
    } finally {
      setLoadingClients(false)
    }
  }

  const handleUnassignClient = async (clientId: string) => {
    if (!selectedAccountant) return

    try {
      await partnerAdminApi.clients.unassign(clientId, selectedAccountant.id)

      // Refresh data
      setAccountantClients(prev => prev.filter(c => c.id !== clientId))
      fetchData()
      setSuccessMessage('Cliente removido com sucesso!')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      console.error('Error unassigning client:', err)
      setError(err.response?.data?.message || 'Erro ao remover atribuicao')
    }
  }

  const handleOpenAddClients = async (accountant: Accountant) => {
    setSelectedAccountant(accountant)
    setShowAddClientsModal(true)
    setLoadingAvailableClients(true)
    setSelectedClientsToAssign([])

    try {
      // Get all clients
      const allClientsRes = await partnerAdminApi.clients.list({ limit: 100 })
      // Get clients already assigned to this accountant
      const assignedClients = await partnerAdminApi.accountants.getClients(accountant.id)
      const assignedIds = new Set(assignedClients.map((c: any) => c.id))

      // Filter to only show unassigned clients
      const available = allClientsRes.clients.filter((c: any) => !assignedIds.has(c.id))
      setAvailableClients(available)
    } catch (err) {
      console.error('Error fetching available clients:', err)
      setAvailableClients([])
    } finally {
      setLoadingAvailableClients(false)
    }
  }

  const handleToggleClientSelection = (clientId: string) => {
    setSelectedClientsToAssign(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    )
  }

  const handleAssignClients = async () => {
    if (!selectedAccountant || selectedClientsToAssign.length === 0) return

    setAssigningClients(true)
    try {
      // Assign each client individually or use bulk assign
      if (selectedClientsToAssign.length === 1) {
        await partnerAdminApi.clients.assign(selectedClientsToAssign[0], selectedAccountant.id)
      } else {
        await partnerAdminApi.clients.bulkAssign(selectedAccountant.id, selectedClientsToAssign)
      }

      setShowAddClientsModal(false)
      setSelectedClientsToAssign([])
      fetchData()
      setSuccessMessage(`${selectedClientsToAssign.length} cliente(s) atribuido(s) com sucesso!`)
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      console.error('Error assigning clients:', err)
      setError(err.response?.data?.message || 'Erro ao atribuir clientes')
    } finally {
      setAssigningClients(false)
    }
  }

  const filteredAccountants = accountants.filter(acc =>
    acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Paginated data
  const totalAccountantsPages = Math.ceil(filteredAccountants.length / itemsPerPage)
  const paginatedAccountants = filteredAccountants.slice(
    (accountantsPage - 1) * itemsPerPage,
    accountantsPage * itemsPerPage
  )

  const totalAssignmentsPages = Math.ceil(assignments.length / itemsPerPage)
  const paginatedAssignments = assignments.slice(
    (assignmentsPage - 1) * itemsPerPage,
    assignmentsPage * itemsPerPage
  )

  // Reset page when search changes
  useEffect(() => {
    setAccountantsPage(1)
  }, [searchTerm])

  // Stats
  const totalClients = accountants.reduce((sum, acc) => sum + acc.clientCount, 0)
  const activeAccountants = accountants.filter(acc => acc.isActive).length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestao de Contadores</h1>
          <p className="text-gray-600 mt-1">
            Gerencie contadores e suas atribuicoes de clientes
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Contadores</p>
              <p className="text-2xl font-bold text-gray-900">{accountants.length}</p>
            </div>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Clientes Atribuidos</p>
              <p className="text-2xl font-bold text-gray-900">{totalClients}</p>
            </div>
            <Link2 className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white p-6 rounded-xl border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar contadores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Accountants Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clientes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ultimo Acesso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedAccountants.map((accountant) => (
                <tr key={accountant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{accountant.name}</div>
                        <div className="text-sm text-gray-500">{accountant.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-center gap-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      {accountant.email}
                    </div>
                    {accountant.phone && (
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        {accountant.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      accountant.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {accountant.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{accountant.clientCount}</span>
                      <span className="text-sm text-gray-500">clientes</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {accountant.lastLogin
                      ? new Date(accountant.lastLogin).toLocaleDateString('pt-BR')
                      : 'Nunca'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewClients(accountant)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        title="Ver clientes atribuidos"
                      >
                        <Eye className="w-4 h-4" />
                        Ver
                      </button>
                      <button
                        onClick={() => handleOpenAddClients(accountant)}
                        className="text-green-600 hover:text-green-900 flex items-center gap-1"
                        title="Atribuir novos clientes"
                      >
                        <UserPlus className="w-4 h-4" />
                        Atribuir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAccountants.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum contador encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Nao ha contadores cadastrados.'}
            </p>
          </div>
        )}

        {/* Pagination for Accountants */}
        {filteredAccountants.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Mostrando {((accountantsPage - 1) * itemsPerPage) + 1} a {Math.min(accountantsPage * itemsPerPage, filteredAccountants.length)} de {filteredAccountants.length} contadores
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAccountantsPage(prev => Math.max(prev - 1, 1))}
                disabled={accountantsPage === 1}
                className={`px-3 py-1 rounded border ${
                  accountantsPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Anterior
              </button>
              <span className="text-sm text-gray-600">
                Pagina {accountantsPage} de {totalAccountantsPages}
              </span>
              <button
                onClick={() => setAccountantsPage(prev => Math.min(prev + 1, totalAccountantsPages))}
                disabled={accountantsPage === totalAccountantsPages}
                className={`px-3 py-1 rounded border ${
                  accountantsPage === totalAccountantsPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Proximo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* All Assignments Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Todas as Atribuicoes</h3>
          <p className="text-sm text-gray-500 mt-1">Visao geral de todas as atribuicoes contador-cliente</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Atribuicao
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{assignment.accountant.name}</div>
                    <div className="text-sm text-gray-500">{assignment.accountant.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{assignment.client.name}</div>
                    <div className="text-sm text-gray-500">{assignment.client.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(assignment.assignedAt).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {assignments.length === 0 && (
          <div className="text-center py-12">
            <Link2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma atribuicao</h3>
            <p className="mt-1 text-sm text-gray-500">
              Atribua clientes aos contadores clicando no botao "Atribuir".
            </p>
          </div>
        )}

        {/* Pagination for Assignments */}
        {assignments.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Mostrando {((assignmentsPage - 1) * itemsPerPage) + 1} a {Math.min(assignmentsPage * itemsPerPage, assignments.length)} de {assignments.length} atribuicoes
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAssignmentsPage(prev => Math.max(prev - 1, 1))}
                disabled={assignmentsPage === 1}
                className={`px-3 py-1 rounded border ${
                  assignmentsPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Anterior
              </button>
              <span className="text-sm text-gray-600">
                Pagina {assignmentsPage} de {totalAssignmentsPages}
              </span>
              <button
                onClick={() => setAssignmentsPage(prev => Math.min(prev + 1, totalAssignmentsPages))}
                disabled={assignmentsPage === totalAssignmentsPages}
                className={`px-3 py-1 rounded border ${
                  assignmentsPage === totalAssignmentsPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Proximo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Clients Modal */}
      {showAssignModal && selectedAccountant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Clientes de {selectedAccountant.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedAccountant.clientCount} clientes atribuidos
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAssignModal(false)
                  setSelectedAccountant(null)
                  setAccountantClients([])
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : accountantClients.length === 0 ? (
                <div className="text-center py-8">
                  <Building className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum cliente atribuido</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Este contador ainda nao possui clientes atribuidos.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {accountantClients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{client.name}</div>
                        <div className="text-sm text-gray-500">{client.email}</div>
                        {client.company && (
                          <div className="text-xs text-gray-400 mt-1">
                            {client.company.name} - CNPJ: {client.company.cnpj}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnassignClient(client.id)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1 text-sm"
                      >
                        <Unlink className="w-4 h-4" />
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Clients Modal */}
      {showAddClientsModal && selectedAccountant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Atribuir Clientes a {selectedAccountant.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Selecione os clientes para atribuir a este contador
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddClientsModal(false)
                  setSelectedAccountant(null)
                  setAvailableClients([])
                  setSelectedClientsToAssign([])
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {loadingAvailableClients ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : availableClients.length === 0 ? (
                <div className="text-center py-8">
                  <Building className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum cliente disponivel</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Todos os clientes ja estao atribuidos a este contador ou nao ha clientes cadastrados.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Select All */}
                  <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg mb-4">
                    <span className="text-sm font-medium text-gray-700">
                      {selectedClientsToAssign.length} de {availableClients.length} selecionados
                    </span>
                    <button
                      onClick={() => {
                        if (selectedClientsToAssign.length === availableClients.length) {
                          setSelectedClientsToAssign([])
                        } else {
                          setSelectedClientsToAssign(availableClients.map(c => c.id))
                        }
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {selectedClientsToAssign.length === availableClients.length ? 'Desmarcar todos' : 'Selecionar todos'}
                    </button>
                  </div>

                  {availableClients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleToggleClientSelection(client.id)}
                      className={`flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-colors ${
                        selectedClientsToAssign.includes(client.id)
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedClientsToAssign.includes(client.id)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedClientsToAssign.includes(client.id) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{client.name}</div>
                        <div className="text-sm text-gray-500">{client.email}</div>
                        {client.cnpj && (
                          <div className="text-xs text-gray-400 mt-1">
                            CNPJ: {client.cnpj}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer with action buttons */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowAddClientsModal(false)
                  setSelectedAccountant(null)
                  setAvailableClients([])
                  setSelectedClientsToAssign([])
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancelar
              </button>
              <button
                onClick={handleAssignClients}
                disabled={selectedClientsToAssign.length === 0 || assigningClients}
                className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${
                  selectedClientsToAssign.length === 0 || assigningClients
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {assigningClients ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Atribuindo...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Atribuir {selectedClientsToAssign.length > 0 ? `(${selectedClientsToAssign.length})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
